import React, { useRef, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import InputWithChecks from "../../shared/InputWithChecks";
import Container from "../../shared/Container";
import editIconWhite from "../../../assets/admin/icons/edit-white.svg";
import BoxBorder from "../../shared/BoxBorder";
import LoadingIndicatorButton from "../../shared/LoadingIndicatorButton";
import {
   fetchAccountSettings,
   updateAccountSettings,
} from "../../../util/admin/httpAdmin";
import SuccessMessage from "../../shared/SuccessMessage";
import { validateRequired } from "../../../util/shared/validation";
import SuccessButton from "../../shared/SuccessButton";
import { queryClient } from "../../../util/admin/httpAdmin";

const PLACEHOLDER_IMAGE = "https://via.placeholder.com/300";

const AccountSettingsFormAdmin = ({ onClose }) => {
   const nameRef = useRef(null);
   const shopNameRef = useRef(null);
   const shopDescriptionRef = useRef(null);
   const shopAddressRef = useRef(null);
   const shopWebsiteUrlRef = useRef(null);
   const contactEmailRef = useRef(null);
   const contactGsmRef = useRef(null);

   const shopMinimumOrderTimeBeforePickupRef = useRef(null);

   const [logoFile, setLogoFile] = useState(null);
   const [bannerFile, setBannerFile] = useState(null);
   const [logoPreview, setLogoPreview] = useState(null);
   const [bannerPreview, setBannerPreview] = useState(null);

   const { data: accountData, isLoading, isError, error } = useQuery({
      queryKey: ["accountSettings"],
      queryFn: fetchAccountSettings,
      onSuccess: (data) => {
      },
   });

   const { mutate, isPending, isError: mutationError, data } = useMutation({
      mutationFn: updateAccountSettings,
      onSuccess: () => {
         queryClient.invalidateQueries(["accountSettings"]);
         setTimeout(() => {
            onClose();
         }, 500);
      },
      onError: (error) => {
         console.error("Error updating account settings:", error);
      },
   });

   const handleImageChange = (event, setImageFile, setImagePreview) => {
      const file = event.target.files[0];
      if (file) {
         setImageFile(file);
         setImagePreview(URL.createObjectURL(file)); // Genereer een tijdelijke preview
      }
   };

   const handleFormSubmit = (e) => {
      e.preventDefault();

      const nameValid = nameRef.current?.validateInput();
      const shopNameValid = shopNameRef.current?.validateInput();
      const shopDescriptionValid = shopDescriptionRef.current?.validateInput();
      const shopAddressValid = shopAddressRef.current?.validateInput();
      const shopMinimumOrderTimeValid = shopMinimumOrderTimeBeforePickupRef.current?.validateInput();

      if (nameValid && shopNameValid && shopDescriptionValid && shopAddressValid && shopMinimumOrderTimeValid) {
         const formData = new FormData();
         formData.append("name", nameRef.current.getValue());
         formData.append("shopName", shopNameRef.current.getValue());
         formData.append("shopDescription", shopDescriptionRef.current.getValue());
         formData.append("shopAddress", shopAddressRef.current.getValue());
         formData.append("websiteUrl", shopWebsiteUrlRef.current.getValue());
         formData.append("contactEmail", contactEmailRef.current.getValue());
         formData.append("contactGsm", contactGsmRef.current.getValue());

         formData.append(
            "shopMinimumOrderTimeBeforePickup",
            shopMinimumOrderTimeBeforePickupRef.current.getValue() || 24
         );

         if (logoFile) formData.append("shopLogo", logoFile);
         if (bannerFile) formData.append("shopBanner", bannerFile);

         mutate(formData);
      }
   };

   if (isLoading) {
      return <p>Laden...</p>;
   }

   if (isError) {
      return <p className="c-r">Er is een fout opgetreden: {error.message}</p>;
   }

   return (
      <Container>
         <BoxBorder extraClasses="mxw-800 m-a g-20">
            <h2>Accountinstellingen Bewerken</h2>
            {mutationError && <p className="c-r">Er is een fout opgetreden bij het opslaan: {mutationError.message}</p>}
            <form className="g-20" onSubmit={handleFormSubmit}>
               <div className="g-1-1 a-i-s">
                  <InputWithChecks
                     ref={nameRef}
                     label="Naam"
                     inputType="text"
                     validate={(value) => validateRequired(value)}
                     placeholder="Voer je naam in"
                     value={accountData?.name || ""}
                  />
                  <InputWithChecks
                     ref={shopNameRef}
                     label="Winkelnaam"
                     inputType="text"
                     validate={(value) => validateRequired(value)}
                     placeholder="Voer de naam van je winkel in"
                     value={accountData?.shopName || ""}
                  />
                  <InputWithChecks
                     ref={shopDescriptionRef}
                     label="Winkelomschrijving"
                     inputType="textarea"
                     validate={(value) => {
                        if (!value) return "Winkelomschrijving is verplicht";
                        if (value.length > 700) return "Winkelomschrijving mag niet langer zijn dan 700 karakters";
                        return "";
                     }}
                     placeholder="Voer een korte omschrijving van je winkel in"
                     value={accountData?.shopDescription || ""}
                  />
                  <InputWithChecks
                     ref={shopWebsiteUrlRef}
                     label="Website url"
                     inputType="text"
                     placeholder="Voer optioneel uw website in"
                     value={accountData?.websiteUrl || ""}
                  />

                  <InputWithChecks ref={contactEmailRef} label="Contact e-mail" inputType="email" validate={validateRequired} placeholder="Voer uw contact e-mail in" value={accountData?.contactEmail || ""} />
                  <InputWithChecks ref={contactGsmRef} label="Contact GSM" inputType="text" validate={validateRequired} placeholder="Voer uw contact GSM in" value={accountData?.contactGsm || ""} />

                  <InputWithChecks
                     ref={shopAddressRef}
                     label="Adres van de winkel"
                     inputType="text"
                     validate={(value) => validateRequired(value)}
                     placeholder="Voer het adres van je winkel in"
                     value={accountData?.shopAddress || ""}
                  />
                  <InputWithChecks
                     ref={shopMinimumOrderTimeBeforePickupRef}
                     label="Minimale tijd voor bestelling (uren)"
                     inputType="number"
                     validate={(value) => {
                        if (value === "") return "";
                        const number = Number(value);
                        if (!Number.isInteger(number)) {
                           return "Voer een geheel getal in";
                        }
                        if (number >= 0 && number <= 24) {
                           return "";
                        } else {
                           return "De maximum-waarde is 24 uur";
                        }
                     }}
                     placeholder="Bijvoorbeeld: 2"
                     value={accountData?.shopMinimumOrderTimeBeforePickup || ""}
                     info="Dit geldt alleen voor bestellingen die vandaag nog opgehaald worden. Bestellingen voor de volgende dag zijn altijd toegestaan."
                  />

                  {/* ✅ Logo Upload */}
                  <div className="g-10">
                     <label>Winkel Logo</label>
                     <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, setLogoFile, setLogoPreview)} />
                     <img src={logoPreview ? logoPreview : `${accountData.shopLogoUrl}?t=${Date.now()}`} alt="Logo preview" className="mxw-150" />
                  </div>

                  {/* ✅ Banner Upload */}
                  <div className="g-10">
                     <label>Winkel Banner</label>
                     <input type="file" accept="image/*" onChange={(e) => handleImageChange(e, setBannerFile, setBannerPreview)} />
                     <img src={bannerPreview ? bannerPreview : `${accountData.shopBannerUrl}?t=${Date.now()}`} alt="Banner preview" className="mxw-300" />
                     {data?.warning && (
                        <p className="c-o" style={{ fontSize: '0.9em', marginTop: '5px' }}>
                           {data.warning}
                        </p>
                     )}
                  </div>
               </div>

               <div>
                  <SuccessButton type="submit" disabled={isPending}>
                     <div className="g-15-1fr">
                        <img src={editIconWhite} alt="edit" />
                        <div className="flex-0-5">
                           Opslaan
                           {isPending && <LoadingIndicatorButton />}
                        </div>
                     </div>
                  </SuccessButton>
               </div>
            </form>
         </BoxBorder>
      </Container>
   );
};

export default AccountSettingsFormAdmin;
